import { useMegaMenu } from '@/hooks/homepage';
import { useProduct, useUpdateInfoGraphic } from '@/hooks/product';
import { useDeliveryOption } from '@/hooks/useDeliveryOptions';
import useHeaderStore from '@/store/header';
import { getDataLayer } from '@/utils/data-layer';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

// Card side of Quick View. The modal lives in AppContainer and reads the header store; a card warms the
// product on hover, syncs the store with what it fetched, and opens the modal on request.
export const useProductQuickView = (seName: string) => {
  const [initProductDetail, setInitProductDetail] = useState(false);
  const [productCardSeName, setProductCardSeName] = useState<string>(seName);
  const { data: megaMenuData } = useMegaMenu();
  const dataLayer = getDataLayer();
  const queryClient = useQueryClient();
  const cartPanel = useHeaderStore((state) => state.cartPanel);
  const openQuickView = useHeaderStore((state) => state.openQuickView);
  const openQuickViewLoading = useHeaderStore((state) => state.openQuickViewLoading);
  const setQuickViewProduct = useHeaderStore((state) => state.setQuickViewProduct);
  const quickViewSeName = useHeaderStore((state) => state.quickViewSeName);
  const { selectedStore, selectedStoreIds } = useDeliveryOption();
  const { data: productDetail, isFetching } = useProduct(
    productCardSeName,
    initProductDetail,
    selectedStoreIds.length > 0 ? selectedStoreIds : selectedStore.Id ? [selectedStore?.Id] : undefined,
  );
  const { mutate: mutateInfoGraphic } = useUpdateInfoGraphic(seName, true);

  useEffect(() => {
    openQuickViewLoading(isFetching);

    if (productDetail?.Id) {
      setQuickViewProduct(productDetail);
    }
  }, [productDetail, isFetching]);

  // The modal swaps the product when the customer picks another colour, by setting the store's seName.
  useEffect(() => {
    setProductCardSeName(quickViewSeName);
  }, [quickViewSeName]);

  useEffect(() => {
    if (cartPanel) openQuickView(false);
  }, [cartPanel]);

  // Pointer arrives on the card: start fetching so the modal has the product ready when it opens.
  const prepareQuickView = () => {
    setInitProductDetail(true);
    openQuickViewLoading(true);
  };

  const showQuickView = () => {
    setProductCardSeName(seName);
    mutateInfoGraphic(seName);
    openQuickView(true);
    queryClient.invalidateQueries({ queryKey: [seName] });
    const obj: { [key: string]: string } = {};
    if (productDetail?.CategoryName) {
      const splittedCategories = productDetail?.CategoryName.split(',');
      splittedCategories.forEach((category, categoryIndex) => {
        obj['item_category' + (categoryIndex ? categoryIndex : '')] = category;
      });
    }
    if (!productDetail?.IsGiftVoucher && productDetail?.Gender) {
      obj['gender'] = productDetail?.Gender;
    }
    dataLayer?.push({
      event: 'view_item',
      ecommerce: {
        currency: 'BDT',
        value: productDetail?.ProductPrice.Price,
        items: [
          {
            item_id: productDetail?.SeName,
            item_name: productDetail?.Name,
            item_sku: productDetail?.Sku,
            item_brand: productDetail?.ProductBrand?.Name,
            price: productDetail?.ProductPrice.Price,
            quantity: productDetail?.StockQuantity,
            item_variant: productDetail?.Variations?.find((x) => x.IsSelected)?.VariantSku,
            size: productDetail?.Variations?.map((variation) => variation?.ProductAttributeValue),
            color: productDetail?.AvailableColors?.map((color) => color?.ColorName),
            ...obj,
          },
        ],
      },
    });
  };

  return { isQuickViewEnabled: !!megaMenuData?.IsQuickViewEnabled, prepareQuickView, showQuickView };
};
